import React, { Component } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { gate } from '../permissions/access';
import toast from '../shared/Toast';
import SettingsPageLayout from './SettingsPageLayout';
import SetupPinModal from './modals/SetupPinModal';
import VerifyPinModal from './modals/VerifyPinModal';
import VerifyResetPinModal from './modals/VerifyResetPinModal';
import ResetPinModal from './modals/ResetPinModal';
import config from '../../config/API';
import { applyServer, lock, unlock, setupPin, errorText } from '../../_utils/receptionMode';
import { visibleGroups, switchesFromCaps } from '../permissions/deskSwitches';
import './SettingsModern.css';
import './ReceptionMode.css';

const headers = () => ({ 'Content-Type': 'application/json', authorization: 'Basic YmV1YXBwOmJldWFwcA==', accessToken: localStorage.getItem('accessToken') });
function post(url, body) {
    return axios.post(url, body, { headers: headers() }).then((res) => {
        const d = res && res.data;
        if (!d || d.statusCode !== 1) { const e = new Error(errorText(d)); e.response = { data: d }; throw e; }
        return d.responseData;
    });
}

/**
 * Settings › Reception: the ONE place reception mode is set up.
 *
 *  1. This device: locked or not, and the button to change that (PIN).
 *  2. What the front desk can see and do: plain switches. Each one saves at
 *     once to the business's Front desk (the same thing the server reads when
 *     a device is locked). Only what the owner can do is ever offered.
 *  3. The PIN: change it, or reset it if forgotten.
 *
 * Locking only affects THIS device; other devices signed in stay as they are.
 */
class ReceptionMode extends Component {
    state = {
        loading: true,
        allowed: false,
        settings: null,      // { hasPinSet, allDevicesLocked, device, frontDesk: { caps, switches, offered, canEdit } }
        modal: null,         // 'setup' | 'pin' | 'change' | 'reset' | 'code' | 'newpin'
        pinPurpose: null,    // 'lock' | 'unlock' | 'legacyOff'
        resetToken: null,
        busy: false,
        saving: null,        // the switch being saved
        savedAt: 0,
    };

    componentDidMount() {
        const hd = this.props.user && this.props.user.userInfo && this.props.user.userInfo.hairdresser;
        // Anyone whose role has `settings` locks their own account's devices.
        const allowed = !!(hd && (hd.isSalonOwner === 1 || gate('settings', false)));
        this.setState({ allowed }, () => {
            if (!allowed) { this.props.history.push('/settings'); return; }
            this.load();
        });
    }

    componentWillUnmount() { clearTimeout(this.savedTimer); }

    load = () => {
        axios.get(config.RECEPTION_MODE_SETTINGS, { headers: headers() })
            .then((res) => {
                const d = res && res.data;
                if (!d || d.statusCode !== 1) throw new Error(errorText(d, 'Could not load reception mode'));
                applyServer(d.responseData);
                this.setState({ settings: d.responseData, loading: false });
            })
            .catch((e) => { toast.error(errorText(e, 'Could not load reception mode')); this.setState({ loading: false }); });
    }

    run = (promise, done) => {
        this.setState({ busy: true });
        return Promise.resolve(promise)
            .then((res) => { this.setState({ busy: false }); if (done) done(res); })
            .catch((e) => { this.setState({ busy: false }); toast.error(errorText(e, "That didn't work")); });
    }

    reload = () => setTimeout(() => window.location.reload(), 150);

    // ---- a switch: saves at once ------------------------------------------
    flip = (key, on) => {
        const { settings, saving } = this.state;
        if (saving) return;
        const fd = (settings && settings.frontDesk) || {};
        // Show it moved straight away; put it back if the save fails.
        const before = fd;
        const switches = { ...(fd.switches || switchesFromCaps(fd.caps)), [key]: on };
        // Bailey off takes approvals with it, on screen and on the server.
        const chain = key === 'bailey' && !on && switches.bailey_approvals;
        if (chain) switches.bailey_approvals = false;
        this.setState({ saving: key, settings: { ...settings, frontDesk: { ...fd, switches } } });
        const save = post(config.RECEPTION_MODE_FRONT_DESK, { key, on })
            .then((next) => (chain ? post(config.RECEPTION_MODE_FRONT_DESK, { key: 'bailey_approvals', on: false }) : next));
        save.then((next) => {
            clearTimeout(this.savedTimer);
            this.savedTimer = setTimeout(() => this.setState({ savedAt: 0 }), 2200);
            this.setState({ saving: null, savedAt: Date.now(), settings: { ...this.state.settings, frontDesk: next } });
        }).catch((e) => {
            toast.error(errorText(e, "That didn't save. Try again."));
            this.setState({ saving: null, settings: { ...this.state.settings, frontDesk: before } });
        });
    }

    // ---- PIN modal outcomes ----------------------------------------------
    onPin = (pin) => {
        const { pinPurpose } = this.state;
        if (pinPurpose === 'lock') {
            return this.run(lock(pin), () => { toast.success('This device is now locked'); this.setState({ modal: null }); this.reload(); });
        }
        if (pinPurpose === 'unlock') {
            return this.run(unlock(pin), () => { toast.success('This device is unlocked'); this.setState({ modal: null }); this.reload(); });
        }
        if (pinPurpose === 'legacyOff') {
            return this.run(post(config.RECEPTION_MODE_SETTINGS, { allDevicesLocked: false, pin }), (s) => {
                toast.success('New sign-ins will no longer start locked'); this.setState({ modal: null, settings: { ...this.state.settings, ...s } });
            });
        }
        return null;
    }

    askPin = (pinPurpose) => this.setState({ modal: 'pin', pinPurpose });

    onSetup = (pin) => this.run(setupPin(pin, true), () => {
        toast.success('PIN set. This device is now locked.');
        this.setState({ modal: null });
        this.reload();
    });

    onChangePin = ({ password: currentPin, pin }) => post(config.RECEPTION_MODE_CHANGE_PIN, { currentPin, pin, confirmPin: pin })
        .then(() => { toast.success('PIN changed'); this.setState({ modal: null }); })
        .catch((e) => { throw new Error(errorText(e, "That didn't work")); });

    onResetWithPassword = ({ password, pin }) => post(config.RECEPTION_MODE_RESET_PIN, { password, pin, confirmPin: pin })
        .then(() => { toast.success('PIN reset'); this.setState({ modal: null }); this.load(); })
        .catch((e) => { throw new Error(errorText(e, "That didn't work")); });

    onResetWithToken = ({ pin }) => post(config.RECEPTION_MODE_RESET_PIN, { resetToken: this.state.resetToken, pin, confirmPin: pin })
        .then(() => { toast.success('PIN reset'); this.setState({ modal: null, resetToken: null }); this.load(); })
        .catch((e) => { throw new Error(errorText(e, "That didn't work")); });

    emailCode = () => this.run(post(config.RECEPTION_MODE_GENERATE_RESET_CODE, {}), (r) => {
        if (r && r.emailError) { toast.warning(r.message); return; }
        toast.success((r && r.message) || 'Code sent');
        this.setState({ modal: 'code' });
    });

    onCode = (resetCode) => this.run(post(config.RECEPTION_MODE_VERIFY_RESET_CODE, { resetCode }), (r) => {
        this.setState({ modal: 'newpin', resetToken: r && r.resetToken });
    });

    renderStatus(device, hasPin) {
        const { busy } = this.state;
        return (
            <div className={`settings-card rm-status${device.locked ? ' is-locked' : ''}`}>
                <div className="rm-status-icon" aria-hidden="true">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="4" y="10.5" width="16" height="10.5" rx="2.5" />
                        {device.locked ? <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" /> : <path d="M8 10.5V7a4 4 0 0 1 7.6-1.7" />}
                    </svg>
                </div>
                <div className="rm-status-text">
                    <h2>{device.locked ? 'This device is locked' : 'This device is not locked'}</h2>
                    <p>
                        {device.locked
                            ? "It shows only what you've switched on below. Unlock it with your PIN to see everything again."
                            : 'Lock it when it sits on a shared front desk: it will show only what you switch on below.'}
                    </p>
                    <p className="rm-status-note">This only affects this device. Your phone and anything else you're signed in on stay as they are.</p>
                </div>
                <div className="rm-status-action">
                    {device.locked ? (
                        <button type="button" className="settings-btn settings-btn-secondary" disabled={busy} onClick={() => this.askPin('unlock')}>Unlock</button>
                    ) : hasPin ? (
                        <button type="button" className="settings-btn settings-btn-primary" disabled={busy} onClick={() => this.askPin('lock')}>Lock this device</button>
                    ) : (
                        <button type="button" className="settings-btn settings-btn-primary" disabled={busy} onClick={() => this.setState({ modal: 'setup' })}>Set a PIN and lock</button>
                    )}
                </div>
            </div>
        );
    }

    renderSwitches(device) {
        const { settings, saving, savedAt } = this.state;
        const fd = settings && settings.frontDesk;
        if (!fd) return null;
        const switches = fd.switches || switchesFromCaps(fd.caps);
        const editable = !!fd.canEdit && !device.locked;
        const groups = visibleGroups(switches, fd.offered);
        let note = null;
        if (device.locked) note = 'Unlock this device to change these.';
        else if (!fd.canEdit) note = fd.shared ? 'These apply at all your locations, so only the business owner can change them.' : 'Only the owner can change these.';
        return (
            <div className="settings-card rm-switches">
                <div className="settings-card-header rm-switches-header">
                    <div>
                        <h2>What the front desk can see and do</h2>
                        <p>When a device is locked, it shows only what's switched on here. Changes save straight away.</p>
                    </div>
                    <span className={`rm-saved${savedAt ? ' is-on' : ''}`} aria-live="polite">{saving ? 'Saving…' : savedAt ? 'Saved' : ''}</span>
                </div>
                {note && <p className="rm-note">{note}</p>}
                {groups.map((g) => (
                    <div key={g.id} className="rm-group">
                        <p className="rm-group-name">{g.name}</p>
                        {g.switches.map((s) => {
                            const on = !!switches[s.key];
                            const needsBailey = s.key === 'bailey_approvals' && !switches.bailey;
                            const disabled = !editable || !!saving || (needsBailey && !on);
                            return (
                                <div key={s.key} className="rm-row">
                                    <span className="rm-row-text">
                                        <span className="rm-row-label">{s.label}</span>
                                        <small>{needsBailey ? 'Switch Bailey on first' : s.hint}</small>
                                    </span>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={on}
                                        aria-label={s.label}
                                        disabled={disabled}
                                        className={`ds-toggle${on ? ' ds-toggle--on' : ''}${disabled ? ' ds-toggle--disabled' : ''}`}
                                        onClick={() => !disabled && this.flip(s.key, !on)}
                                    >
                                        <span className="ds-toggle-knob" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    }

    render() {
        const { loading, allowed, settings, modal, pinPurpose, busy } = this.state;
        if (!allowed) {
            return (
                <SettingsPageLayout activeTab="reception-mode" {...this.props}>
                    <h4>Unauthorized Access</h4>
                    <p>You do not have permission to access this page.</p>
                </SettingsPageLayout>
            );
        }
        const device = (settings && settings.device) || { locked: false };
        const hasPin = !!(settings && settings.hasPinSet);
        const pinTitles = {
            lock: ['Lock this device', "Enter your PIN. You'll need it again to unlock."],
            unlock: ['Unlock this device', 'Enter your PIN to unlock this device.'],
            legacyOff: ['Stop locking new sign-ins', 'Enter your PIN to save this change.']
        }[pinPurpose] || ['Enter your PIN', ''];

        return (
            <SettingsPageLayout activeTab="reception-mode" {...this.props}>
                {loading ? (
                    <div className="settings-card"><p>Loading…</p></div>
                ) : (
                    <>
                        {this.renderStatus(device, hasPin)}
                        {this.renderSwitches(device)}

                        {hasPin && (
                            <div className="settings-card">
                                <div className="settings-card-header">
                                    <h2>PIN</h2>
                                    <p>The 4-digit PIN locks and unlocks this device. Five wrong tries pause it for 15 minutes. Forgot it? Reset it with your account password.</p>
                                </div>
                                <div className="rm-pin-links">
                                    <button type="button" className="settings-btn-link" onClick={() => this.setState({ modal: 'change' })}>Change PIN</button>
                                    <button type="button" className="settings-btn-link" onClick={() => this.setState({ modal: 'reset' })}>Forgot PIN?</button>
                                </div>
                            </div>
                        )}

                        {settings && settings.allDevicesLocked && (
                            <div className="settings-card">
                                <div className="settings-card-header">
                                    <h2>Every sign-in starts locked</h2>
                                    <p>Reception mode used to lock every device signed in to this account. Devices locked that way stay locked until each one is unlocked with the PIN, and new sign-ins still start locked.</p>
                                </div>
                                <button type="button" className="settings-btn-link" disabled={busy} onClick={() => this.askPin('legacyOff')}>Stop locking new sign-ins</button>
                            </div>
                        )}
                    </>
                )}

                {modal === 'setup' && <SetupPinModal onClose={() => this.setState({ modal: null })} onSetupPin={this.onSetup} />}
                {modal === 'pin' && (
                    <VerifyPinModal
                        onClose={() => this.setState({ modal: null })}
                        onVerifyPin={this.onPin}
                        onForgotPin={() => this.setState({ modal: 'reset' })}
                        title={pinTitles[0]}
                        message={pinTitles[1]}
                    />
                )}
                {modal === 'change' && <ResetPinModal mode="change" onClose={() => this.setState({ modal: null })} onSubmit={this.onChangePin} />}
                {modal === 'reset' && <ResetPinModal onClose={() => this.setState({ modal: null })} onSubmit={this.onResetWithPassword} onEmailCode={this.emailCode} />}
                {modal === 'code' && <VerifyResetPinModal onClose={() => this.setState({ modal: null })} onVerifyResetPin={this.onCode} />}
                {modal === 'newpin' && <ResetPinModal resetToken={this.state.resetToken} onClose={() => this.setState({ modal: null, resetToken: null })} onSubmit={this.onResetWithToken} />}
            </SettingsPageLayout>
        );
    }
}

export default connect((state) => ({ ...state }))(ReceptionMode);
